import { z } from "zod";
import { currentUserSchema } from "./session";

/**
 * How a sign-in attempt ended, as the SignInStates board labels it. Each one has a single next action
 * and a support reference. A provider error code never appears here: the page shows the reference id
 * and nothing else.
 */
export const signInOutcomes = [
  "redirecting",
  "sso-not-configured",
  "session-expired",
  "account-not-allowed",
  "provider-unavailable",
  "signed-out",
] as const;
export const signInOutcomeSchema = z.enum(signInOutcomes);
export type SignInOutcome = z.infer<typeof signInOutcomeSchema>;

/**
 * What the API tells the page about the current session. The token itself is never part of this: it
 * travels only in the `HttpOnly` cookie. `outcome` is set when the person is not signed in, so the
 * page can show the matching state.
 */
export const sessionStateSchema = z.object({
  authenticated: z.boolean(),
  user: currentUserSchema.nullable(),
  outcome: signInOutcomeSchema.nullable(),
  supportReference: z.string().nullable(),
});
export type SessionState = z.infer<typeof sessionStateSchema>;

/** A session lasts 8 hours. Drafts survive a sign-out for 24 hours. */
export const SESSION_TTL_HOURS = 8;
export const DRAFT_RETENTION_HOURS = 24;

/**
 * A return destination after sign-in is safe only when it stays inside this site: a path on the
 * same origin with no host, no scheme, and no protocol-relative form. Anything else lands on `/`,
 * so a crafted link can never bounce the person elsewhere. Shared here so the sign-in page and
 * the callback cannot drift.
 */
export function safeReturnTo(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return null;
  if (raw.includes("\\")) return null;
  const [path, ...rest] = raw.split("#");
  if (rest.length > 1) return null;
  const [pathname, ...queries] = (path ?? "").split("?");
  if (queries.length > 1) return null;
  if (!pathname || /[\s<>]/.test(pathname)) return null;
  if (pathname.includes("?") || pathname.includes("#")) return null;
  const query = queries[0] ?? "";
  if (/[\s<>]/.test(query)) return null;
  // A nested return keeps bouncing after sign-in, so anything carrying one is dropped.
  if (/(^|&)returnTo=/i.test(query)) return null;
  const hash = rest[0] ?? "";
  if (/[\s<>]/.test(hash)) return null;
  return raw;
}
